import type { Conversation } from '@/types';

/**
 * Choose the conversation record to drive the planner gate, reconciling the
 * sidebar list row (`listRow`, from useGetConversations) against the
 * self-healing by-id query (`byId`, from useGetConversationById).
 *
 * Both sources now self-heal: each subscribes to #conversation# and refetches on
 * a debounced WS invalidate + a 60s fallback poll. But they are separate query
 * instances with separate debounce timers, so during the 2s debounce / poll
 * windows they can briefly disagree on `status`.
 *
 * Resolution:
 *  - If only one source exists, return it.
 *  - If both exist, return the list row — EXCEPT apply a DOWNGRADE-ONLY status
 *    override: when the list row is 'active' but the by-id record has a known
 *    non-'active' status, trust the by-id status (prevents a wedged-'active'
 *    gate if an active->idle MODIFY reaches by-id first).
 *  - Never override upward: on the send path the list row flips to 'active' via
 *    the WS INSERT item faster than by-id invalidates, so an upgrade override
 *    would briefly resurrect a stale 'idle' and re-open Enter mid-run.
 */
export function mergeSelectedConversation(
  listRow: Conversation | undefined,
  byId: Conversation | undefined
): Conversation | undefined {
  if (!listRow) return byId;
  if (!byId) return listRow;

  if (listRow.status === 'active' && byId.status && byId.status !== 'active') {
    return { ...listRow, status: byId.status };
  }
  return listRow;
}
